// services/audioOverviews.ts
//
// Audio Overviews (ORA-735): two-host podcast episodes generated from selected
// lectures. The shared contract lives with the ORA-735 brief; ora-be must match
// it, or bump it and tell this file.
//
//   GET    /api/v5/audio-overviews/config        200 AudioOverviewConfig
//   POST   /api/v5/audio-overviews               202 { overview }
//                                                (409 ALREADY_EXISTS + { overview }
//                                                when this exact lecture set has one)
//   GET    /api/v5/audio-overviews?lectureIds=a,b&match=any|exact&limit=&cursor=
//                                                200 { items, nextCursor }
//                                                (match=exact: same lecture set only)
//   GET    /api/v5/audio-overviews/:id           200 AudioOverviewSummary
//   GET    /api/v5/audio-overviews/:id/audio     200 { url, expiresAt, durationSec, mimeType }
//   GET    /api/v5/audio-overviews/:id/script    200 { success, data: AudioOverviewScript }
//                                                (409 NOT_READY before a script exists)
//   PUT    /api/v5/audio-overviews/:id/progress  204
//   POST   /api/v5/audio-overviews/:id/retry     202 { overview }
//   DELETE /api/v5/audio-overviews/:id           204
//   GET    /api/v5/audio-overviews/articles/:articleId
//                                                200 { overview | null, locked,
//                                                canGenerate, blockedBy }
//   GET    /api/v5/audio-overviews/lectures/:lectureId
//                                                200 { overview | null, locked }
//                                                (the lecture's READY shared episode;
//                                                404 when the caller cannot read the
//                                                lecture, 403 when a member is denied)
//   POST   /api/v5/audio-overviews/articles/:articleId
//                                                200 { overview } (already READY)
//                                                202 { overview } (started or in flight)
//                                                (403 AUDIO_OVERVIEW_UPGRADE_REQUIRED on
//                                                the free plan; 429 ARTICLE_AUDIO_DAILY_LIMIT
//                                                | ARTICLE_AUDIO_IN_PROGRESS_LIMIT, or the
//                                                caller's own AUDIO_OVERVIEW_* caps;
//                                                404 AUDIO_OVERVIEW_NOT_FOUND for a missing
//                                                or empty article → NOT_FOUND here)
//
// Notes:
// - Article episodes (ORA-905) are ONE shared Deep Dive per Library article:
//   `source: 'article'`, `articleId`, `articleTitle`, no lectures, no focus
//   notes. The /:id routes serve them to any paid caller (progress is per
//   caller); DELETE is refused for them. Older servers omit the three fields,
//   so the parser defaults `source` to 'lecture'. `LOCKED` is tolerated as a
//   paywall code but not sent today.
// - Lecture episodes Ora made in advance are shared too (`shared: true`,
//   `source: 'lecture'`, one lecture): the /:id routes play them for anyone
//   who can read the lecture, progress is per caller, and DELETE and retry
//   answer 404. They are never in `GET /`.
// - When the feature is off for the caller, EVERY route answers a bare 404.
//   `fetchAudioOverviewConfig` turns that into `null` ("feature hidden").
// - Errors carry `{ code, message, ...extra }`. Every rejection from this
//   module is an `AudioOverviewApiError` with a known `code`, so callers
//   switch on codes rather than on axios internals.
// - Responses are read tolerantly: an item without an id or a known status is
//   dropped instead of half-rendered.

import axiosInstance from './axiosCreator';
import {
  AUDIO_OVERVIEWS_ROUTE,
  audioOverviewRoute,
  articleAudioOverviewRoute,
  lectureAudioOverviewRoute,
  AudioOverviewApiError,
  asRecord,
  asString,
  asNumber,
  toAudioOverviewError,
  isOneOf,
  parseAudioOverviewSummary,
  parseArticleAudioOverviewResponse,
  requireSummary,
  parseConfig,
  parseScriptTiming,
  parseScriptSegments,
  parseScriptLectures,
} from './audioOverviewParsing';
import {
  AUDIO_OVERVIEW_STATUSES,
  type AudioOverviewAudioSource,
  type AudioOverviewConfig,
  type AudioOverviewListPage,
  type AudioOverviewScript,
  type AudioOverviewStatus,
  type AudioOverviewSummary,
  type ArticleAudioOverviewResponse,
  type CreateArticleAudioOverviewResponse,
  type CreateAudioOverviewInput,
  type LectureAudioOverviewResponse,
} from '@/types/AudioOverview';

export {
  AUDIO_OVERVIEWS_ROUTE,
  AUDIO_OVERVIEW_MAX_LECTURE_IDS,
  AudioOverviewAlreadyExistsError,
  AudioOverviewApiError,
  articleAudioOverviewRoute,
  audioOverviewRoute,
  lectureAudioOverviewRoute,
  parseAudioOverviewSummary,
  toAudioOverviewError,
} from './audioOverviewParsing';
export type {
  AudioOverviewDailyLimitReason,
  AudioOverviewErrorCode,
  AudioOverviewErrorDetails,
} from './audioOverviewParsing';

/**
 * The caller's limits and plan state, or `null` when the feature is hidden
 * for them (a bare 404). Other failures reject.
 */
export const fetchAudioOverviewConfig =
  async (): Promise<AudioOverviewConfig | null> => {
    try {
      const response = await axiosInstance.get(
        `${AUDIO_OVERVIEWS_ROUTE}/config`
      );

      return parseConfig(response.data);
    } catch (error) {
      const apiError = toAudioOverviewError(error);
      if (apiError.code === 'NOT_FOUND') return null;
      throw apiError;
    }
  };

export const createAudioOverview = async (
  input: CreateAudioOverviewInput
): Promise<AudioOverviewSummary> => {
  const focusNotes = input.focusNotes?.trim();

  const body: CreateAudioOverviewInput = {
    lectureIds: [...new Set(input.lectureIds)],
    format: input.format,
    ...(input.examId ? { examId: input.examId } : {}),
    ...(input.format !== 'BRIEF' && input.length
      ? { length: input.length }
      : {}),
    ...(focusNotes ? { focusNotes } : {}),
  };

  try {
    const response = await axiosInstance.post(AUDIO_OVERVIEWS_ROUTE, body);
    return requireSummary(asRecord(response.data)?.overview);
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

export const fetchArticleAudioOverview = async (
  articleId: string
): Promise<ArticleAudioOverviewResponse> => {
  try {
    const response = await axiosInstance.get(
      articleAudioOverviewRoute(articleId)
    );

    return parseArticleAudioOverviewResponse(response.data);
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

/** The lecture's shared episode Ora made in advance, if it has one READY. */
export const fetchLectureAudioOverview = async (
  lectureId: string
): Promise<LectureAudioOverviewResponse> => {
  try {
    const response = await axiosInstance.get(
      lectureAudioOverviewRoute(lectureId)
    );

    const record = asRecord(response.data) ?? {};
    return {
      overview: parseAudioOverviewSummary(record.overview),
      locked: record.locked === true,
    };
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

export const createArticleAudioOverview = async (
  articleId: string
): Promise<CreateArticleAudioOverviewResponse> => {
  try {
    const response = await axiosInstance.post(
      articleAudioOverviewRoute(articleId)
    );

    return {
      overview: requireSummary(asRecord(response.data)?.overview),
      status: response.status === 202 ? 202 : 200,
    };
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

/**
 * How `lectureIds` filters the list: `any` (the server default) keeps
 * episodes that include any of them; `exact` keeps only episodes whose
 * lecture set is exactly them, in any order (ORA-810).
 */
export type AudioOverviewListMatch = 'any' | 'exact';

export interface ListAudioOverviewsParams {
  lectureIds?: string[];
  /** Only sent with `lectureIds`; the server rejects `exact` without them. */
  match?: AudioOverviewListMatch;
  limit?: number;
  cursor?: string | null;
}

export const listAudioOverviews = async ({
  lectureIds,
  match,
  limit,
  cursor,
}: ListAudioOverviewsParams = {}): Promise<AudioOverviewListPage> => {
  const params: Record<string, string | number> = {};
  if (lectureIds && lectureIds.length > 0) {
    params.lectureIds = lectureIds.join(',');
    if (match) params.match = match;
  }
  if (typeof limit === 'number') params.limit = limit;
  if (cursor) params.cursor = cursor;

  try {
    const response = await axiosInstance.get(AUDIO_OVERVIEWS_ROUTE, {
      params,
    });

    const body = asRecord(response.data);

    const items = Array.isArray(body?.items)
      ? body.items.flatMap((item) => {
          const summary = parseAudioOverviewSummary(item);
          return summary ? [summary] : [];
        })
      : [];

    return { items, nextCursor: asString(body?.nextCursor) };
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

export const getAudioOverview = async (
  id: string
): Promise<AudioOverviewSummary> => {
  try {
    const response = await axiosInstance.get(audioOverviewRoute(id));
    return requireSummary(response.data);
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

/** A fresh signed URL for the episode. Access is re-checked on every call. */
export const getAudioOverviewAudio = async (
  id: string
): Promise<AudioOverviewAudioSource> => {
  try {
    const response = await axiosInstance.get(audioOverviewRoute(id, '/audio'));
    const body = asRecord(response.data);
    const url = asString(body?.url);
    const expiresAt = asString(body?.expiresAt);
    if (!url || !expiresAt) {
      throw new AudioOverviewApiError(
        'UNKNOWN',
        null,
        'Malformed audio source response'
      );
    }

    return {
      url,
      expiresAt,
      durationSec: asNumber(body?.durationSec) ?? 0,
      mimeType: asString(body?.mimeType) ?? 'audio/mpeg',
    };
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

/**
 * The episode's transcript: segments of turns with their fact ids and
 * estimated start times, chunk timing, and the lecture map for fact ids.
 * Rejects with NOT_READY (409) before a script exists and UPGRADE_REQUIRED
 * (403) for a locked caller, like the audio.
 */
export const getAudioOverviewScript = async (
  id: string
): Promise<AudioOverviewScript> => {
  try {
    const response = await axiosInstance.get(audioOverviewRoute(id, '/script'));
    // The route answers `{ success, data }`; accept the bare payload too.
    const body = asRecord(response.data);
    const data = asRecord(body?.data) ?? body;
    if (!data || !Array.isArray(data.segments)) {
      throw new AudioOverviewApiError(
        'UNKNOWN',
        null,
        'Malformed audio overview script response'
      );
    }

    const timing = parseScriptTiming(data.timing);

    return {
      id: asString(data.id) ?? id,
      title: asString(data.title) ?? '',
      status: isOneOf<AudioOverviewStatus>(AUDIO_OVERVIEW_STATUSES, data.status)
        ? data.status
        : 'READY',
      segments: parseScriptSegments(data.segments, timing !== null),
      timing,
      lectures: parseScriptLectures(data.lectures),
    };
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

/** What happened to a progress save. */
export type AudioOverviewProgressSaveResult =
  | 'saved'
  /** 403 UPGRADE_REQUIRED: the plan lapsed while listening. */
  | 'upgrade-required'
  | 'failed';

/**
 * Saves the resume position. Fire-and-forget: it never rejects, and it goes
 * out with `keepalive` so the engine's `pagehide` save survives the tab
 * closing. A 403 UPGRADE_REQUIRED is reported so the caller can stop playback.
 */
export const saveAudioOverviewProgress = async (
  id: string,
  progress: { positionSec: number; completed?: boolean }
): Promise<AudioOverviewProgressSaveResult> => {
  try {
    await axiosInstance.put(
      audioOverviewRoute(id, '/progress'),
      {
        positionSec: Math.max(0, Math.floor(progress.positionSec)),
        ...(progress.completed ? { completed: true } : {}),
      },
      { adapter: 'fetch', fetchOptions: { keepalive: true } }
    );

    return 'saved';
  } catch (error) {
    return toAudioOverviewError(error).code ===
      'AUDIO_OVERVIEW_UPGRADE_REQUIRED'
      ? 'upgrade-required'
      : 'failed';
  }
};

export const retryAudioOverview = async (
  id: string
): Promise<AudioOverviewSummary> => {
  try {
    const response = await axiosInstance.post(audioOverviewRoute(id, '/retry'));
    return requireSummary(asRecord(response.data)?.overview);
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};

export const deleteAudioOverview = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(audioOverviewRoute(id));
  } catch (error) {
    throw toAudioOverviewError(error);
  }
};
